import { createSignature } from "./crypto";
import { isBrowser, openFeedbackBoard } from "./open";
import type {
	FdbackConfig,
	FdbackUser,
	OpenOptions,
	SignedLoginData,
} from "./types";

const DEFAULT_BASE_URL = "https://app.fdback.io";
const SIGNATURE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Fdback SDK client for authenticating users into the feedback board
 */
export class Fdback {
	private readonly workspaceId: string;
	private readonly workspaceSecret?: string;
	private readonly baseUrl: string;

	constructor(config: FdbackConfig) {
		if (!config.workspaceId) {
			throw new Error("[Fdback] workspaceId is required");
		}

		this.workspaceId = config.workspaceId;
		this.workspaceSecret = config.workspaceSecret;
		this.baseUrl = config.baseUrl ?? DEFAULT_BASE_URL;

		// Warn if using secret in browser
		if (this.workspaceSecret && isBrowser()) {
			console.warn(
				"[Fdback] WARNING: workspaceSecret should not be used in browser code. " +
					"The secret will be visible to users. Use server-side signing instead.",
			);
		}
	}

	/**
	 * Check if SDK is configured for server-side usage (has secret)
	 */
	isServerMode(): boolean {
		return !!this.workspaceSecret;
	}

	/**
	 * Get the public feedback board URL (no authentication)
	 */
	getBoardUrl(): string {
		return `${this.baseUrl}/b/${this.workspaceId}`;
	}

	/**
	 * Create signed login data that can be used from browser
	 * @server Server-side only - requires workspaceSecret
	 */
	async createSignedLoginData(user: FdbackUser): Promise<SignedLoginData> {
		if (!this.workspaceSecret) {
			throw new Error(
				"[Fdback] workspaceSecret is required for createSignedLoginData. " +
					"This method should only be called server-side.",
			);
		}

		if (!user.email) {
			throw new Error("[Fdback] user.email is required");
		}

		const timestamp = Date.now().toString();
		const body = JSON.stringify({
			email: user.email,
			name: user.name,
			avatar: user.avatar,
		});

		const signature = await createSignature(
			this.workspaceSecret,
			timestamp,
			body,
		);

		return {
			url: `${this.baseUrl}/api/auth/sdk-login`,
			headers: {
				"x-workspace-id": this.workspaceId,
				"x-timestamp": timestamp,
				"x-signature": signature,
			},
			body,
			expiresAt: Date.now() + SIGNATURE_EXPIRY_MS,
		};
	}

	/**
	 * Get login URL for server-side redirects
	 * Returns a URL that the server can redirect to
	 * @server Server-side only - requires workspaceSecret
	 */
	async getLoginUrl(user: FdbackUser): Promise<string> {
		const signedData = await this.createSignedLoginData(user);

		// Encode signed data as URL-safe base64 for query param
		const encodedData = btoa(JSON.stringify(signedData));
		return `${this.baseUrl}/api/auth/sdk-login?data=${encodeURIComponent(encodedData)}`;
	}

	/**
	 * Open the feedback board in browser with authentication
	 * @browser Browser-side only
	 */
	open(signedData: SignedLoginData, options?: OpenOptions): Window | null {
		if (!isBrowser()) {
			throw new Error(
				"[Fdback] open() can only be called in browser environment",
			);
		}

		return openFeedbackBoard(signedData, options);
	}

	/**
	 * Authenticate and open feedback board directly
	 * Only works if workspaceSecret is configured (not recommended for browser)
	 */
	async openDirect(
		user: FdbackUser,
		options?: OpenOptions,
	): Promise<Window | null> {
		if (!isBrowser()) {
			throw new Error(
				"[Fdback] openDirect() can only be called in browser environment",
			);
		}

		const signedData = await this.createSignedLoginData(user);
		return this.open(signedData, options);
	}
}
